import { PriceDetailPosition, PriceDetailType, ProductInterface } from '@bytel/product-wall/types';

import { getFundingOrder, getFundingPhone } from '@services/products';

import { SapiMapper } from '@mappers/sapi';
import { SapicMapper } from '@mappers/sapic';

import { PalpatineDetailedPlanType } from '@app-types/api/palpatine';
import { SapiPlanType } from '@app-types/api/sapi';
import { WallType } from '@app-types/config';
import { ContractType } from '@app-types/contract';
import { DiscountEnumType, DiscountType } from '@app-types/discount';
import { IFundingDetail } from '@app-types/funding';
import { GalliumData } from '@app-types/gallium';
import {
    MobilePlanDetailWithProvenanceType,
    MobilePlanProvenanceType,
    MobilePlanType,
    PlanType,
} from '@app-types/plan';
import { Product, ProductChildFull } from '@app-types/product';
import { SalesWallType } from '@app-types/sales';

import { paths } from '@helpers/path';
import { LockedPlanValue } from '@helpers/plan-lock';

import { MarketingType, PlanSubTypeId, PlanTypeId } from '@constants/plan';

const createProductDisplay = (
    product: Product,
    plan?: MobilePlanDetailWithProvenanceType,
    contract?: ContractType,
): Product => {
    const newProduct: Product = { ...product };

    if (plan?.plan) {
        const isSmartChange = plan.plan?.subTypeId === PlanSubTypeId.SMART_CHANGE;
        const isFunding = Boolean(product.priceInfo?.top);
        const isPlanFromUrl = plan.origin === MobilePlanProvenanceType.URL;
        const isPlanFromCart = plan.origin === MobilePlanProvenanceType.CART;
        const isPremiumRenewal = contract?.subscription.isPremium ?? false;

        newProduct.priceInfo = {
            unit: '€',
            top: !isPlanFromUrl && ((!isPremiumRenewal && !isPlanFromCart) || isFunding) ? 'À partir de' : undefined,
        };

        if (isSmartChange) {
            // Thanks to Trilogy only doing client side, we have to hardcode the HTML...
            newProduct.decoration = (
                <span
                    className='icon is-medium is-circled is-main has-background-main-fade'
                    title='Téléphone éligible Smart Change'
                >
                    <i className='tri-smart-change' aria-hidden='true'></i>
                </span>
            );
        }

        if (!product.priceDetail && newProduct.priceInfo?.top) {
            newProduct.priceDetail = {
                position: PriceDetailPosition.BESIDE,
                type: PriceDetailType.BUTTON,
            };
        }

        if (product._marketingMeta?.stickers?.some((s) => s && !s.displayWithPlan)) {
            newProduct.stickers = product.stickers?.filter((_, index) => {
                const meta = product._marketingMeta?.stickers?.[index];
                return meta === undefined || meta.displayWithPlan;
            });
        }

        if (product._marketingMeta?.stamp && !product._marketingMeta.stamp.displayWithPlan) {
            newProduct.stamp = undefined;
        }
    } else {
        if (product._marketingMeta?.stickers?.some((s) => s && !s.displayWithoutPlan)) {
            newProduct.stickers = product.stickers?.filter((_, index) => {
                const meta = product._marketingMeta?.stickers?.[index];
                return meta === undefined || meta.displayWithoutPlan;
            });
        }

        if (product._marketingMeta?.stamp && !product._marketingMeta.stamp.displayWithoutPlan) {
            newProduct.stamp = undefined;
        }
    }

    return newProduct;
};

export const getDisplayProducts = (
    products: Product[],
    plan?: MobilePlanDetailWithProvenanceType,
    contract?: ContractType,
): Product[] => {
    return products.map((product) => createProductDisplay(product, plan, contract));
};

export const findProduct = (products: Product[], id: ProductInterface['id']): Product | undefined => {
    return products.find((product) => product.id === id);
};

export const getSimilarProductsLink = (type: WallType, { manufacturerCode }: ProductChildFull, isRenewal: boolean) => {
    if (manufacturerCode) {
        switch (type) {
            case WallType.PHONE:
                return paths.phoneFilter(isRenewal, manufacturerCode);
            case WallType.ACCESSORY:
                return paths.accessoryFilter(manufacturerCode);
            case WallType.HOTSPOT:
                return paths.hotspotWall;
            case WallType.SALES_PHONE:
                return paths.salesFilter(SalesWallType.PHONE, manufacturerCode);
            case WallType.SALES_ACCESSORY:
                return paths.salesFilter(SalesWallType.ACCESSORY, manufacturerCode);
            default:
                return paths.phoneWall;
        }
    } else {
        switch (type) {
            case WallType.PHONE:
                return isRenewal ? paths.renewalPhoneWall : paths.phoneWall;
            case WallType.ACCESSORY:
                return paths.accessoryWall;
            case WallType.HOTSPOT:
                return paths.hotspotWall;
            case WallType.SALES_PHONE:
                return paths.sales(SalesWallType.PHONE);
            case WallType.SALES_ACCESSORY:
                return paths.sales(SalesWallType.ACCESSORY);
            default:
                return paths.phoneWall;
        }
    }
};

export const mapApiPlanToMobilePlanType = (
    planSapi: SapiPlanType,
    planSapic?: MobilePlanType,
    phoneGencode?: string,
    planCms?: PalpatineDetailedPlanType,
): MobilePlanType => {
    let fundings: IFundingDetail[] = [];
    if (phoneGencode && planSapic) {
        fundings = getFundingPhone(planSapic, phoneGencode)?.sort(
            (a: IFundingDetail, b: IFundingDetail) => getFundingOrder(a.type) - getFundingOrder(b.type),
        );
    }

    const isMultiline = planSapic?.details.discounts.some((discount) =>
        discount.marketingTypes.includes(MarketingType.MULTILINE),
    );

    const basePlan: Partial<MobilePlanType> = planSapic ? planSapic : {};
    // @TODO: no mapping here - use repo / mapper
    const detailData: Partial<MobilePlanType> = {
        image: '',
        recommended: false,
        inStock: true,
        gencode: planSapi.gencode,
        name: planSapic?.name ?? planSapi.name,
        sexyName: `${isMultiline ? '' : 'Forfait '}${planSapi.short_description}`,
        typeId: planSapi.type_id as PlanTypeId,
        dataEnvelope: planSapi.data_envelope || '',
        dataSize: planSapi.data_size,
        hasLowPrice: planSapi.low_prices_wall,
        hasDoubleSim: planSapi.double_sim,
        isDarwin: planSapi.is_darwin,
        subTypeId: planSapi.sub_type_id ?? PlanSubTypeId.CLASSIC,
        locked: planSapi.locked as LockedPlanValue,
        smsMms: planSapi.sms_mms,
        unlimitedCalls: planSapi.unlimited_call,
        internationalCall: planSapi.international_call,
        wordingBtv: planSapi.wording_btv,
        fromAbroad: planSapi.from_abroad,
        isExcluPro: planSapi.exclu_pro,
        price: planSapi.price,
        maxConnections: planSapi.max_connection,
        detailsDescription: planSapi.catchy_description,
        shortDescription: planSapi.short_description,
        fundings,
        urlKey: planSapi.url_key,
        commercialRange: planSapi.commercial_range,
        entityId: planSapi.entity_id,
        planFlag: planSapi.plan_flag,
        obligation: planSapi.obligation,
        obligationLabel: SapicMapper.getObligationLabel(planSapi.obligation),
        cmsContent: SapiMapper.mapCmsPlan(planCms),
    };

    return { ...basePlan, ...detailData } as MobilePlanType;
};

export const getDiscountValue = (discounts: DiscountType[], type: DiscountEnumType): number => {
    return discounts.reduce((acc, discount) => {
        return discount.type === type ? acc + discount.value : acc;
    }, 0);
};

export const getProductNameByGencode = (gencode: string, products: PlanType[]): string | undefined => {
    const product = products.find((plan) => plan.gencode === gencode);
    return product?.name;
};

export function getProductName(brand?: string, name: string = '') {
    return brand ? `${brand} ${name}` : name;
}

export function getNeededElementGencodes(product: Product | undefined | null): string[] {
    return product?.neededElements?.map((el) => el.gencode) || [];
}

export const addRenewalInfoToPlans = (plans: MobilePlanType[], allPlansGallium: GalliumData) => {
    return plans.map((plan) => {
        const isPlanOwned = allPlansGallium?.sourceOffer.offerId === plan.gencode;
        const newPlan = allPlansGallium?.simulations?.find((offer) => offer.offerId === plan.gencode);
        const source = isPlanOwned ? allPlansGallium.sourceOffer : newPlan;

        const renewalDiscounts = source?.advantageDiscounts;
        const hasRenewalDiscounts = Math.abs(source?.discountedPrice?.priceIncludingTax ?? 0) > 0;

        const position = isPlanOwned
            ? 0
            : allPlansGallium?.simulations.map((offer) => offer.offerId).indexOf(plan.gencode) + 1;

        return {
            ...plan,
            isOwned: isPlanOwned,
            position: position,
            renewalDiscounts: renewalDiscounts,
            hasRenewalDiscounts: hasRenewalDiscounts,
        };
    });
};
